import type { RequiredGeneric } from '@pba/react-utils';
import { startOfDay } from 'date-fns';
import { Alert, Col, DatePicker, type DatePickerDateType, Form, FormSection, Icon, Row, Select } from 'ui';
import { useCreateDurationOptions, useEventOverMidnightAlert, useStartDateRules } from './hooks';
import { type ExtendedStartDateFormValues, StartDateFormContent } from './start-date-form-content';

export type ExtendedDurationFormValues = {
  duration: number;
  startDate: DatePickerDateType;
} & ExtendedStartDateFormValues;

const DurationFormContent = <T extends ExtendedDurationFormValues = never>(_: RequiredGeneric<T>) => {
  const startDate = Form.useWatch<ExtendedDurationFormValues['startDate'] | undefined>('startDate');
  const startTime = Form.useWatch<ExtendedDurationFormValues['startTime'] | undefined>('startTime');
  const duration = Form.useWatch<ExtendedDurationFormValues['duration'] | undefined>('duration');
  const durationOptions = useCreateDurationOptions({ startDate, startTime });
  const eventOverMidnightAlert = useEventOverMidnightAlert({ duration, startDate, startTime });
  const startDateRules = useStartDateRules();

  return (
    <FormSection title={{ children: 'Duration', icon: <Icon icon="time-line" /> }}>
      <Row gutter="xs">
        <Col span={8}>
          <Form.Item<ExtendedDurationFormValues> label="Date" name="startDate" rules={startDateRules}>
            <DatePicker fullWidth minimum={startOfDay(new Date())} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <StartDateFormContent<ExtendedDurationFormValues> />
        </Col>
        <Col span={8}>
          <Form.Item<ExtendedDurationFormValues> label="Duration" name="duration" rules={[{ required: true }]}>
            <Select optionLabelProp="optionLabel" options={durationOptions} />
          </Form.Item>
        </Col>
      </Row>
      {eventOverMidnightAlert ? (
        <Form.Item<ExtendedDurationFormValues>>
          <Alert title={eventOverMidnightAlert} />
        </Form.Item>
      ) : null}
    </FormSection>
  );
};

export default DurationFormContent;
